import { getWebApiErrorMessage } from "@faire/web--source/common/getErrorMessage";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { logError } from "@faire/web--source/common/logging";
import { NumberParser } from "@faire/web--source/common/NumberParser";
import { trackEvent } from "@faire/web--source/common/trackEvent";
import { IOption } from "@faire/web--source/slate/Dropdown";
import { useMutation as usePostAdditionalInfoMutation } from "@faire/web-api--source/api/brand-lead/additional-info/post-hooks";
import { IBrandLeadApplicationAdditionalInfo } from "@faire/web-api--source/indigofair/data/IBrandLeadApplicationAdditionalInfo";
import { ICreateBrandLeadApplicationAdditionalInfoRequest } from "@faire/web-api--source/indigofair/data/ICreateBrandLeadApplicationAdditionalInfoRequest";
import { useState, useCallback } from "react";

export const useMakerSignUpAdditionalQuestions = (brandLeadToken: string) => {
  const [yearEstablished, setYearEstablished] = useState<number | undefined>();
  const [instagramHandle, setInstagramHandle] = useState<string | undefined>();
  const [faireValues, setFaireValues] = useState<Set<string>>(new Set());
  const [additionalNotes, setAdditionalNotes] = useState<string | undefined>();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { strictLocalize } = useStrictLocalization();
  const { mutate: postAdditionalInfo, isPending: isPostingAdditionalInfo } =
    usePostAdditionalInfoMutation({
      onSuccess: () => {
        trackEvent("maker-application/tell-us-more/submit");
        setFormSubmitted(true);
      },
      onError: (error) => {
        const fallbackMessage = strictLocalize({
          defaultMessage:
            "Something went wrong. Please try again or visit our Help Center if you have any questions.",
        });

        setErrors((prev) => ({
          ...prev,
          submission: getWebApiErrorMessage(
            error,
            strictLocalize,
            fallbackMessage
          ),
        }));

        logError(error);
      },
    });

  const handleYearEstablishedChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const numberParser = new NumberParser();
      const value = numberParser.parse(e.target.value);

      if (Number.isNaN(value) && e.target.value) {
        setErrors((prev) => ({
          ...prev,
          yearEstablished: strictLocalize({
            defaultMessage: "Please enter a number",
          }),
        }));
        setYearEstablished(undefined);
      } else {
        setYearEstablished(value);
        setErrors((prev) => {
          const { yearEstablished, ...rest } = prev;
          return rest;
        });
      }
    },
    [strictLocalize]
  );

  const handleInstagramHandleChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Using this regex for validating Instagram handles: https://regexr.com/3cg7r
      const regex = /^(?!.*\.\.)(?!.*\.$)[^\W][\w.]{0,29}$/;
      if (!e.target.value.match(regex)) {
        setErrors((prev) => ({
          ...prev,
          instagramHandle: strictLocalize({
            defaultMessage: "Please enter a valid instagram handle",
          }),
        }));
        setInstagramHandle(undefined);
      } else {
        setInstagramHandle(e.target.value);
        setErrors((prev) => {
          const { instagramHandle, ...rest } = prev;
          return rest;
        });
      }
    },
    [strictLocalize]
  );

  const isFaireValueSelected = useCallback(
    (option: IOption): boolean => {
      return faireValues.has(option.value);
    },
    [faireValues]
  );

  const onChangeFaireValue = useCallback(
    (option: IOption) => {
      if (isFaireValueSelected(option)) {
        setFaireValues((prev) => {
          const updatedSet = new Set(prev);
          updatedSet.delete(option.value);
          return updatedSet;
        });
      } else {
        setFaireValues((prev) => new Set([...prev, option.value]));
      }
    },
    [isFaireValueSelected]
  );

  const onSave = useCallback(async () => {
    const { submission, ...formErrors } = errors;

    if (Object.keys(formErrors).length > 0) {
      setErrors((prev) => ({
        ...prev,
        submission: strictLocalize({
          defaultMessage: "Please address all values in red.",
        }),
      }));
      return;
    } else {
      setErrors({});
    }

    const request = ICreateBrandLeadApplicationAdditionalInfoRequest.build({
      additional_info: IBrandLeadApplicationAdditionalInfo.build({
        brand_lead_token: brandLeadToken,
        year_established: yearEstablished,
        instagram_handle: instagramHandle,
        faire_values: Array.from(faireValues),
        additional_notes: additionalNotes,
      }),
    });

    postAdditionalInfo([request]);
  }, [
    errors,
    strictLocalize,
    postAdditionalInfo,
    brandLeadToken,
    yearEstablished,
    instagramHandle,
    faireValues,
    additionalNotes,
  ]);

  return {
    yearEstablished,
    instagramHandle,
    faireValues,
    additionalNotes,
    handleYearEstablishedChanged,
    handleInstagramHandleChanged,
    isFaireValueSelected,
    onChangeFaireValue,
    setAdditionalNotes,
    errors,
    onSave,
    formSubmitted,
    isPostingAdditionalInfo,
  };
};
